import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { invoicesService } from '../../api/apiClient';
import { useToast } from '../../context/ToastContext';
import { Card, CardHeader, CardTitle, CardBody } from '../../components/UI/Card';
import { Button } from '../../components/UI/Button';
import { Input, Textarea, Select } from '../../components/UI/Input';
import { Spinner } from '../../components/UI/Spinner';
import {
  Plus,
  Trash2,
  Save,
  ArrowLeft,
  UserPlus,
  Sparkles,
} from 'lucide-react';

export const InvoiceForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { success, error: toastError } = useToast();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [limitReached, setLimitReached] = useState(false);

  // Reference data
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [products, setProducts] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [nextNumber, setNextNumber] = useState('000001');

  // Form State
  const [formData, setFormData] = useState({
    invo_date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    customer_id: '',
    project_id: '',
    currency_id: '1',
    notes: '',
    // Inline new customer fields
    is_new_customer: false,
    customer_name: '',
    customer_email: '',
    customer_address1: '',
    customer_address2: '',
  });

  // Items State
  const [items, setItems] = useState([
    { product_id: '', description: '', quantity: 1, price: 0, total: 0 }
  ]);

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const loadFormData = async () => {
      setLoading(true);
      try {
        const createRes = await invoicesService.getCreateData();
        if (createRes && createRes.data) {
          if (createRes.data.limitReached) {
            setLimitReached(true);
            setLoading(false);
            return;
          }

          setCustomers(createRes.data.customers || []);
          setProjects(createRes.data.projects || []);
          setProducts(createRes.data.products || []);
          setCurrencies(createRes.data.currencies || []);
          if (createRes.data.nextNumber) setNextNumber(createRes.data.nextNumber);
          
          if (createRes.data.currencies?.length > 0) {
            setFormData(prev => ({
              ...prev,
              currency_id: prev.currency_id || String(createRes.data.currencies[0].id)
            }));
          }
        }

        if (isEdit) {
          const editRes = await invoicesService.get(id);
          if (editRes && editRes.data && editRes.data.invoice) {
            const inv = editRes.data.invoice;
            setFormData({
              invo_date: inv.invo_date || '',
              due_date: inv.due_date || '',
              customer_id: String(inv.customer_id || ''),
              project_id: inv.project_id ? String(inv.project_id) : '',
              currency_id: String(inv.currency_id || '1'),
              notes: inv.notes || '',
              is_new_customer: false,
              customer_name: '',
              customer_email: '',
              customer_address1: '',
              customer_address2: '',
            });

            if (inv.invoiceItems && Array.isArray(inv.invoiceItems) && inv.invoiceItems.length > 0) {
              setItems(inv.invoiceItems.map(item => ({
                product_id: item.product_id ? String(item.product_id) : '',
                description: item.description || '',
                quantity: Number(item.quantity || 1),
                price: Number(item.price || 0),
                total: Number(item.price || 0) * Number(item.quantity || 1)
              })));
            }
          }
        }
      } catch (err) {
        toastError(err.message || 'Error al cargar datos del formulario.');
      } finally {
        setLoading(false);
      }
    };

    loadFormData();
  }, [id, isEdit, toastError]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleItemChange = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;

    if (field === 'product_id') {
      const selectedProd = products.find(p => String(p.id) === String(value));
      if (selectedProd) {
        updated[index].description = selectedProd.name;
        updated[index].price = Number(selectedProd.price || 0);
      }
    }

    const qty = parseFloat(updated[index].quantity) || 0;
    const prc = parseFloat(updated[index].price) || 0;
    updated[index].total = Number((qty * prc).toFixed(2));

    setItems(updated);
  };

  const addItemRow = () => {
    setItems(prev => [
      ...prev,
      { product_id: '', description: '', quantity: 1, price: 0, total: 0 }
    ]);
  };

  const removeItemRow = (index) => {
    if (items.length <= 1) return;
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const calculateGrandTotal = () => {
    return items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrors({});

    const total = calculateGrandTotal();

    const payload = {
      invo_date: formData.invo_date,
      due_date: formData.due_date,
      project_id: formData.project_id ? formData.project_id : null,
      currency_id: formData.currency_id,
      notes: formData.notes,
      items: JSON.stringify(items),
      total: total,
      customer_id: formData.is_new_customer ? 'new' : formData.customer_id,
      ...(formData.is_new_customer ? {
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_address1: formData.customer_address1,
        customer_address2: formData.customer_address2,
      } : {})
    };

    try {
      if (isEdit) {
        await invoicesService.update(id, payload);
        success('Factura actualizada correctamente.');
      } else {
        await invoicesService.create(payload);
        success('Factura emitida exitosamente.');
      }
      navigate('/invoices');
    } catch (err) {
      if (err.errors) {
        setErrors(err.errors);
      }
      toastError(err.data?.message || err.message || 'Error al guardar factura.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Spinner size="lg" label="Cargando formulario..." className="py-20" />;
  }

  if (limitReached) {
    return (
      <div className="max-w-xl mx-auto py-12 text-center space-y-6">
        <div className="p-8 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xl space-y-4">
          <div className="w-14 h-14 rounded-2xl bg-amber-50 dark:bg-amber-950 text-amber-600 flex items-center justify-center mx-auto">
            <Sparkles className="w-7 h-7" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
            Límite del Plan Gratuito Alcanzado
          </h2>
          <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed max-w-md mx-auto">
            Has emitido el máximo de facturas permitidas en la versión gratuita (5 facturas). Suscríbete al Plan Pro para facturar sin límites.
          </p>
          <div className="pt-2 flex items-center justify-center gap-3">
            <Link to="/invoices">
              <Button variant="secondary" size="sm">Volver a Facturas</Button>
            </Link>
            <Link to="/subscriptions">
              <Button variant="primary" size="sm" className="bg-[#08707A] hover:bg-[#065B63]">
                Ver Planes de Suscripción
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const grandTotal = calculateGrandTotal();
  const selectedCurrency = currencies.find(c => String(c.id) === String(formData.currency_id));
  const currencySymbol = selectedCurrency?.symbol || '$';

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/invoices">
            <Button variant="ghost" size="sm" icon={ArrowLeft}>
              Volver
            </Button>
          </Link>
          <div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100 tracking-tight">
              {isEdit ? `Editar Factura #${id}` : `Nueva Factura (${nextNumber})`}
            </h1>
            <p className="text-xs text-slate-500">
              {isEdit ? 'Modifica los datos del comprobante fiscal' : 'Emite un comprobante formal para tu cliente'}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Main Details Card */}
        <Card>
          <CardHeader>
            <CardTitle subtitle="Información general y plazos de pago">
              Encabezado de la Factura
            </CardTitle>
          </CardHeader>

          <CardBody className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Input
                label="Fecha de Emisión"
                type="date"
                name="invo_date"
                value={formData.invo_date}
                onChange={handleInputChange}
                error={errors.invo_date?.[0]}
                required
              />

              <Input
                label="Fecha de Vencimiento"
                type="date"
                name="due_date"
                value={formData.due_date}
                onChange={handleInputChange}
                error={errors.due_date?.[0]}
              />

              <Select
                label="Moneda"
                name="currency_id"
                value={formData.currency_id}
                onChange={handleInputChange}
                error={errors.currency_id?.[0]}
                required
              >
                {currencies.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.code} ({c.symbol}) - {c.name}
                  </option>
                ))}
              </Select>

              <Select
                label="Proyecto (Opcional)"
                name="project_id"
                value={formData.project_id}
                onChange={handleInputChange}
                error={errors.project_id?.[0]}
              >
                <option value="">-- Sin Proyecto Asociado --</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </Select>
            </div>

            {/* Customer Section */}
            <div className="pt-3 border-t border-slate-100 dark:border-slate-800">
              <div className="flex items-center justify-between mb-3">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Cliente Destinatario
                </label>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, is_new_customer: !prev.is_new_customer }))}
                  className="text-xs text-[#08707A] dark:text-teal-400 font-bold hover:underline flex items-center gap-1"
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  {formData.is_new_customer ? 'Seleccionar Cliente Existente' : '+ Registrar Cliente Nuevo Aquí'}
                </button>
              </div>

              {!formData.is_new_customer ? (
                <Select
                  name="customer_id"
                  value={formData.customer_id}
                  onChange={handleInputChange}
                  error={errors.customer_id?.[0]}
                  required={!formData.is_new_customer}
                >
                  <option value="">-- Selecciona un Cliente del Directorio --</option>
                  {customers.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.full_name} {c.email ? `(${c.email})` : ''}
                    </option>
                  ))}
                </Select>
              ) : (
                <div className="p-4 rounded-xl bg-teal-50/50 dark:bg-teal-950/30 border border-teal-100 dark:border-teal-900/60 space-y-3 animate-fade-in">
                  <Input
                    label="Nombre Completo o Razón Social"
                    name="customer_name"
                    placeholder="Empresa / Contacto"
                    value={formData.customer_name}
                    onChange={handleInputChange}
                    error={errors.customer_name?.[0]}
                    required={formData.is_new_customer}
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input
                      label="Correo Electrónico"
                      type="email"
                      name="customer_email"
                      placeholder="cliente@email.com"
                      value={formData.customer_email}
                      onChange={handleInputChange}
                    />
                    <Input
                      label="Dirección Principal"
                      name="customer_address1"
                      placeholder="Av. Principal #456"
                      value={formData.customer_address1}
                      onChange={handleInputChange}
                      error={errors.customer_address1?.[0]}
                      required={formData.is_new_customer}
                    />
                  </div>
                </div>
              )}
            </div>
          </CardBody>
        </Card>

        {/* Dynamic Items Card */}
        <Card>
          <CardHeader actions={
            <Button type="button" variant="secondary" size="sm" icon={Plus} onClick={addItemRow}>
              Agregar Concepto
            </Button>
          }>
            <CardTitle subtitle="Artículos facturados y deducción automática de inventario">
              Partidas y Conceptos
            </CardTitle>
          </CardHeader>

          <CardBody className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 font-bold uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                  <tr>
                    <th className="py-3 px-4 w-48">Producto Inventario</th>
                    <th className="py-3 px-4">Descripción del Concepto</th>
                    <th className="py-3 px-4 w-24">Cantidad</th>
                    <th className="py-3 px-4 w-32">Precio Unit.</th>
                    <th className="py-3 px-4 w-32 text-right">Subtotal</th>
                    <th className="py-3 px-4 w-12 text-center"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {items.map((item, idx) => (
                    <tr key={idx} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
                      <td className="py-2.5 px-4">
                        <select
                          className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-2 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:ring-1 focus:ring-brand-500 focus:outline-none"
                          value={item.product_id}
                          onChange={(e) => handleItemChange(idx, 'product_id', e.target.value)}
                        >
                          <option value="">(Manual sin inventario)</option>
                          {products.map(p => (
                            <option key={p.id} value={p.id}>
                              {p.name} (Stock: {p.stock})
                            </option>
                          ))}
                        </select>
                      </td>

                      <td className="py-2.5 px-4">
                        <input
                          type="text"
                          required
                          placeholder="Descripción del concepto o servicio..."
                          className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-2.5 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:ring-1 focus:ring-brand-500 focus:outline-none"
                          value={item.description}
                          onChange={(e) => handleItemChange(idx, 'description', e.target.value)}
                        />
                      </td>

                      <td className="py-2.5 px-4">
                        <input
                          type="number"
                          step="any"
                          min="0.01"
                          required
                          className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-2 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:ring-1 focus:ring-brand-500 focus:outline-none text-right font-mono"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(idx, 'quantity', e.target.value)}
                        />
                      </td>

                      <td className="py-2.5 px-4">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          required
                          className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-2 py-1.5 text-xs text-slate-800 dark:text-slate-200 focus:ring-1 focus:ring-brand-500 focus:outline-none text-right font-mono font-bold"
                          value={item.price}
                          onChange={(e) => handleItemChange(idx, 'price', e.target.value)}
                        />
                      </td>

                      <td className="py-2.5 px-4 text-right font-black text-slate-900 dark:text-slate-100 font-mono text-sm">
                        {currencySymbol} {Number(item.total).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>

                      <td className="py-2.5 px-4 text-center">
                        <button
                          type="button"
                          disabled={items.length <= 1}
                          onClick={() => removeItemRow(idx)}
                          className="text-slate-400 hover:text-red-600 disabled:opacity-30 disabled:hover:text-slate-400 transition-colors p-1"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Total Footer Banner */}
            <div className="p-4 bg-slate-50 dark:bg-slate-800/40 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Total Factura
              </span>
              <span className="text-xl font-black text-slate-900 dark:text-slate-100 font-mono">
                {currencySymbol} {Number(grandTotal).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
          </CardBody>
        </Card>

        {/* Notes & Submit */}
        <Card>
          <CardBody className="space-y-4">
            <Textarea
              label="Notas, Cuentas Bancarias e Instrucciones de Pago"
              name="notes"
              placeholder="Ej: Transferir a la cuenta corriente del Banco XYZ #000-111-222..."
              value={formData.notes}
              onChange={handleInputChange}
              rows={3}
            />

            <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100 dark:border-slate-800">
              <Link to="/invoices">
                <Button variant="secondary" disabled={submitting}>
                  Cancelar
                </Button>
              </Link>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                icon={Save}
                isLoading={submitting}
                className="bg-[#08707A] hover:bg-[#065B63]"
              >
                {isEdit ? 'Guardar Cambios' : 'Emitir Factura'}
              </Button>
            </div>
          </CardBody>
        </Card>
      </form>
    </div>
  );
};
